import { useState, useRef, useCallback } from 'react';
import { speak as ttsspeak } from '../services/tts';

const SILENCE_THRESHOLD = 15;
const SILENCE_DURATION  = 10000;

const LANG_MAP = {
  it: 'it-IT', en: 'en-US', es: 'es-ES',
  fr: 'fr-FR', de: 'de-DE', pt: 'pt-PT', nl: 'nl-NL',
};
function toLangCode(lang) { return LANG_MAP[lang] || 'it-IT'; }

function isMobile() {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

export function useVoice({ onTranscript, onVolumeChange, lang = 'it' }) {
  const [state, setState]   = useState('idle');
  const [error, setError]   = useState(null);

  const audioCtxRef     = useRef(null);
  const analyserRef     = useRef(null);
  const streamRef       = useRef(null);
  const animRef         = useRef(null);
  const recognitionRef  = useRef(null);
  const silenceRef      = useRef(null);
  const silenceTimerRef = useRef(null);
  const hasSpeechRef    = useRef(false);
  const transcriptRef   = useRef('');
  const interimRef      = useRef('');
  const activeRef       = useRef(false);

  const stop = useCallback((send = true) => {
    activeRef.current = false;
    cancelAnimationFrame(animRef.current);
    clearTimeout(silenceTimerRef.current);
    if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch {} recognitionRef.current = null; }
    if (streamRef.current)      { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }

    // Cattura il contesto audio da chiudere e libera subito il ref
    const ctxToClose    = audioCtxRef.current;
    audioCtxRef.current = null;
    analyserRef.current = null;

    onVolumeChange(0);
    silenceRef.current = null;

    const transcript  = transcriptRef.current || interimRef.current;
    const shouldSend  = send && (hasSpeechRef.current || !!transcript);
    hasSpeechRef.current  = false;
    transcriptRef.current = '';
    interimRef.current    = '';

    const dispatch = () => {
      if (shouldSend) {
        setState('processing');
        onTranscript(transcript || '[voce rilevata]');
      } else {
        setState('idle');
      }
    };

    // Desktop: aspetta che l'AudioContext sia completamente chiuso prima del fetch
    // (Chrome può bloccare richieste di rete durante il teardown audio)
    if (ctxToClose) {
      ctxToClose.close().then(dispatch).catch(dispatch);
    } else {
      dispatch();
    }
  }, [onTranscript, onVolumeChange]);

  // Desktop: analisi volume reale via AudioContext
  const loopAnalyse = useCallback(() => {
    if (!analyserRef.current) return;
    const data = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(data);
    const avg = data.reduce((s, v) => s + v, 0) / data.length;
    onVolumeChange(avg);

    if (avg > SILENCE_THRESHOLD) {
      hasSpeechRef.current = true;
      silenceRef.current   = null;
    } else if (hasSpeechRef.current) {
      if (!silenceRef.current) silenceRef.current = Date.now();
      if (Date.now() - silenceRef.current >= SILENCE_DURATION) { stop(true); return; }
    }
    animRef.current = requestAnimationFrame(loopAnalyse);
  }, [stop, onVolumeChange]);

  const startRecognition = useCallback((mobile = false) => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;

    const rec = new SR();
    rec.lang          = toLangCode(lang);
    rec.continuous    = true;
    rec.interimResults = true;

    rec.onresult = e => {
      let final = '';
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) final += e.results[i][0].transcript;
        else interim += e.results[i][0].transcript;
      }
      if (final) {
        transcriptRef.current = final;
        interimRef.current    = '';
      } else if (interim) {
        interimRef.current = interim;
      }

      // Su desktop segna speech rilevato anche da onresult (non solo dal volume)
      if (final || interim) hasSpeechRef.current = true;

      if (mobile && (final || interim)) {
        onVolumeChange(35 + Math.random() * 35);
        clearTimeout(silenceTimerRef.current);
        if (final) {
          silenceTimerRef.current = setTimeout(() => {
            if (activeRef.current) stop(true);
          }, SILENCE_DURATION);
        }
      }
    };

    rec.onerror = (e) => {
      if (e.error === 'no-speech' || e.error === 'aborted') {
        if (mobile) onVolumeChange(0);
        return;
      }
      if (e.error === 'not-allowed') setError('Microfono non autorizzato.');
    };

    rec.onend = () => {
      if (mobile) onVolumeChange(0);
      if (activeRef.current && recognitionRef.current === rec) {
        try { rec.start(); } catch {}
      }
    };

    try { rec.start(); } catch {}
    recognitionRef.current = rec;
  }, [lang, stop, onVolumeChange]);

  const start = useCallback(async () => {
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    setError(null);
    transcriptRef.current = '';
    hasSpeechRef.current  = false;
    activeRef.current     = true;

    const mobile = isMobile();

    if (!mobile) {
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch {
        setError('Microfono non accessibile. Controlla i permessi.');
        setState('idle');
        activeRef.current = false;
        return;
      }
      streamRef.current = stream;
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      if (ctx.state === 'suspended') await ctx.resume();
      audioCtxRef.current = ctx;
      const analyser = ctx.createAnalyser(); analyser.fftSize = 256;
      analyserRef.current = analyser;
      ctx.createMediaStreamSource(stream).connect(analyser);
      loopAnalyse();
    }

    startRecognition(mobile);
    setState('listening');
  }, [loopAnalyse, startRecognition]);

  const toggle = useCallback(() => {
    if (state === 'listening') stop(true);
    else if (state === 'idle') start();
    else if (state === 'speaking') {
      // Secondo tap durante la risposta vocale: interrompe subito l'audio
      // invece di aspettare la fine naturale della sintesi (SA-VOICE-INTERRUPT-01)
      if (window.speechSynthesis) window.speechSynthesis.cancel();
      setState('idle');
    }
  }, [state, start, stop]);

  const speak = useCallback((text) => {
    setState('speaking');
    ttsspeak(text, {
      rate:    1.05,
      pitch:   1,
      lang,
      onEnd:   () => setState('idle'),
      onError: () => setState('idle'),
    });
  }, [lang]);

  return { state, error, toggle, speak, setIdle: () => setState('idle') };
}
